import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Secure token storage using sessionStorage (cleared on tab close) with basic obfuscation
const tokenKey = "___at";
const storeToken = (token) => {
  try { sessionStorage.setItem(tokenKey, btoa(token)); } catch (e) { console.warn("Token store failed:", e.message); }
};
const readToken = () => {
  try { const v = sessionStorage.getItem(tokenKey); return v ? atob(v) : null; } catch (e) { console.warn("Token read failed:", e.message); return null; }
};
const clearToken = () => {
  try { sessionStorage.removeItem(tokenKey); localStorage.removeItem("token"); } catch (e) { console.warn("Token clear failed:", e.message); }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Migrate any legacy localStorage token
      const legacy = localStorage.getItem("token");
      if (legacy) {
        storeToken(legacy);
        localStorage.removeItem("token");
      }
      const token = readToken();
      if (!token) {
        // sessionStorage is per-tab, so a new tab starts with no bearer token.
        // The backend reads the httpOnly access_token cookie first and only then
        // the Authorization header, and cookies ARE shared across tabs, so cookie
        // auth alone is enough here. Note /auth/refresh only sets a new cookie,
        // it does not return a token in the body, so never look for one.
        try {
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(data);
          setLoading(false);
          return;
        } catch (e) {
          // access_token cookie missing or expired. Try the 7-day refresh cookie.
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
            setLoading(false);
            return;
          } catch (e2) {
            // Genuinely logged out.
          }
        }
        setUser(false);
        setLoading(false);
        return;
      }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(data);
    } catch (e) {
      console.error("Auth check failed:", e.message);
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    storeToken(data.token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    storeToken(data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout request failed:", e.message);
    }
    clearToken();
    setUser(false);
  }, []);

  const deleteAccount = useCallback(async () => {
    const token = readToken();
    await axios.delete(`${API}/auth/account`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
    clearToken();
    setUser(false);
  }, []);

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, deleteAccount, checkAuth
  }), [user, loading, login, register, logout, deleteAccount, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

export { API, BACKEND_URL };
